/**
 * LeftShield Portal - App Root Component
 *
 * Multi-tenant customer-facing portal for runtime enforcement and security.
 * Customers use this app to view their enforcement posture, manage findings,
 * and monitor delivery governance status.
 */
import { useState, useCallback, useEffect, createContext, useContext, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import {
  FluentProvider, Spinner, makeStyles, tokens,
  Toaster, useToastController, useId,
  Toast, ToastTitle, ToastBody,
} from '@fluentui/react-components';
import { MsalProvider } from '@azure/msal-react';
import { lightTheme, darkTheme, injectBrandCSSProperties } from './theme/solution-theme';
import { MainLayout } from './components/layout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { UsersRolesPage } from './pages/UsersRolesPage';
import { CompliancePage } from './pages/CompliancePage';
import { SecurityPage } from './pages/SecurityPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { GeneralSettingsPage } from './pages/GeneralSettingsPage';
import { AdminPage } from './pages/AdminPage';
import { SSOSettingsPage } from './pages/SSOSettingsPage';
import { DiagnosticPage } from './pages/DiagnosticPage';
import { EvidencePage } from './pages/EvidencePage';
import { DeveloperToolsPage } from './features/developer-tools/components';
import { IntegrationsPage } from './features/integrations/components';
import { msalInstance, AuthProvider, useAuth } from './features/authentication';
import { ProtectedRoute } from './features/authentication/components/ProtectedRoute';
import { TenantProvider } from './contexts/TenantContext';
import { UserInitProvider } from './providers/UserInitProvider';

// Phase 1 — HIGH Priority Pages
import { PolicyRunsPage } from './pages/PolicyRunsPage';
import { ExecutiveRiskPage } from './pages/ExecutiveRiskPage';
import { FrameworksPage } from './pages/FrameworksPage';
// Control Coverage disabled — requires manual mapping data that doesn't exist yet
// import { ControlCoveragePage } from './pages/ControlCoveragePage';
import { ScorecardPage } from './pages/ScorecardPage';
import { PRBotPage } from './pages/PRBotPage';
import { EnvironmentsPage } from './pages/EnvironmentsPage';

// Phase 2 — MEDIUM Priority Pages
import { ControlRoomPage } from './pages/ControlRoomPage';
import { BoardViewPage } from './pages/BoardViewPage';
import { DependenciesPage } from './pages/DependenciesPage';
import { AttestationsPage } from './pages/AttestationsPage';
import { AIAdvisorPage } from './pages/AIAdvisorPage';
import { MergeGateSettingsPage } from './pages/MergeGateSettingsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { NotificationPreferencesPage } from './pages/NotificationPreferencesPage';
import { BillingPage } from './pages/BillingPage';
import { ReportsPage } from './pages/ReportsPage';
import { AssurancePage } from './pages/AssurancePage';
import { GuardrailsPage } from './pages/GuardrailsPage';

// PM Review Features (F034, F035)
import { ConstitutionEnginePage } from './pages/ConstitutionEnginePage';

// Review 004 — New Pages (F065, F070, F071, F072, F074)
// GovernanceCommandPage (/command) — F045: resolved as the "Control Plane x2" F5 duplicate of
// HomePage (/dashboard). No longer routed directly; /command redirects to /dashboard below.
// The component file is untouched (capability preserved on the surviving page — F045 §3.1).
import { AttackPathsPage } from './pages/AttackPathsPage';
import { AutorepairPage } from './pages/AutorepairPage';
import { ProfilePage } from './pages/ProfilePage';

// F051 — Policy Intelligence Network
import { PolicyIntelligenceDashboardPage } from './pages/PolicyIntelligenceDashboardPage';
import { PolicyRecommendationsPage } from './pages/PolicyRecommendationsPage';

// F052 — Benchmark Insights
import { BenchmarkInsightsPage } from './pages/BenchmarkInsightsPage';

// Review 006 — New Pages (F090, F100-F103, F110-F113)
import { GovernanceLoopPage } from './pages/GovernanceLoopPage';
import { EssentialEightPage } from './pages/EssentialEightPage';
import { DataExposureRadarPage } from './pages/DataExposureRadarPage';

// Review 007 — AI Governance Layer
import { AIGovernanceOverviewPage } from './pages/AIGovernanceOverviewPage';
import { AIDecisionDetailPage } from './pages/AIDecisionDetailPage';
import { AIConstitutionRulesPage } from './pages/AIConstitutionRulesPage';
import { AISimulationReviewPage } from './pages/AISimulationReviewPage';

// §12746 Wave 1 — GUID-routed detail pages (stubs; Wave 2 ports full drawer content in)
import { FrameworkDetailPage } from './pages/detail/FrameworkDetailPage';
import { FindingDetailPage } from './pages/detail/FindingDetailPage';
import { VulnerabilityDetailPage } from './pages/detail/VulnerabilityDetailPage';
import { RunDetailPage } from './pages/detail/RunDetailPage';
// #12772 — last §12746 drawer→route conversion (ControlCoveragePage's inline
// ControlDetailDrawer), now that the portal-reachable control-detail endpoint exists.
import { ControlDetailPage } from './pages/detail/ControlDetailPage';

// Phase 3 — LOW Priority Placeholder Pages
// F045: DigitalTwinPage (/twin), WorkflowPage (/workflow), AutonomousPage (/autonomous), and
// LearningPage (/learning) are no longer routed directly — each resolved as an F5 duplicate
// (see src/ia/iaSurfaceRegistry.ts). Their routes redirect to the canonical surface below;
// the component files are untouched.
import { ScannersPage } from './pages/ScannersPage';
import { GraphPage } from './pages/GraphPage';
import { AgentRegistryPage } from './pages/AgentRegistryPage';
import { McpGovernancePage } from './pages/McpGovernancePage';
import { AutonomousGovernancePage } from './pages/AutonomousGovernancePage';
import { RuntimeGovernancePage } from './pages/RuntimeGovernancePage';
import { TrustPage } from './pages/TrustPage';
import { GovernanceDriftPage } from './pages/GovernanceDriftPage';
import { ExecutionSimulatorPage } from './pages/ExecutionSimulatorPage';
import { CollectorsPage } from './pages/CollectorsPage';
import { PredictivePage } from './pages/PredictivePage';

// Review 009 — AI Code Governance Gate
import { CodeGovernanceDashboardPage } from './pages/CodeGovernanceDashboardPage';
import { CodeGovernancePullRequestsPage } from './pages/CodeGovernancePullRequestsPage';
import { CodeGovernancePoliciesPage } from './pages/CodeGovernancePoliciesPage';
import { CodeGovernanceDemoPage } from './pages/CodeGovernanceDemoPage';

// Review 008 — Governance Posture, Exceptions, Audit, Persona, Assistant
import { GovernancePosturePage } from './pages/GovernancePosturePage';
import { PolicyExceptionsPage } from './pages/PolicyExceptionsPage';
import { AuditWorkspacePage } from './pages/AuditWorkspacePage';
import { PersonaProvider, usePersona } from './features/persona';
import { GlobalAssistant } from './components/assistant/GlobalAssistant';

// Import global styles
import './styles/teams-theme.css';
import './styles/fluent-overrides.css';

// Inject brand CSS custom properties on load
injectBrandCSSProperties();

// =============================================================================
// THEME CONTEXT (light/dark toggle)
// =============================================================================
type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'dark',
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

// Global toast dispatcher — bridged from MutationCache (module scope) to React via GlobalToaster
let dispatchToastRef: ((content: ReactNode, options?: object) => void) | null = null;

const mutationCache = new MutationCache({
  onError: (error) => {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred';
    dispatchToastRef?.(
      <Toast>
        <ToastTitle>Action failed</ToastTitle>
        <ToastBody>{message}</ToastBody>
      </Toast>,
      { intent: 'error' as const, timeout: 5000 },
    );
  },
});

// React Query client configuration
const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

// =============================================================================
// GLOBAL TOASTER — wires MutationCache onError to Fluent UI Toaster
// =============================================================================
function GlobalToaster() {
  const toasterId = useId('global-toaster');
  const { dispatchToast } = useToastController(toasterId);

  useEffect(() => {
    dispatchToastRef = dispatchToast;
    return () => { dispatchToastRef = null; };
  }, [dispatchToast]);

  return <Toaster toasterId={toasterId} position="bottom-end" timeout={5000} />;
}

// =============================================================================
// PERSONA-AWARE ROOT LANDING (F045 — persona lenses: role-based default landing)
// =============================================================================
/**
 * Redirects an authenticated user landing on "/" to their persona's default route
 * (`/api/portal/me/persona`, F044) — e.g. Compliance/GRC lands on Evidence & Assurance,
 * Engineering lands on Estate — instead of everyone always landing on the same dashboard.
 * `/dashboard` itself is untouched and remains a stable, directly-linkable route for
 * everyone (F045 does not fork the UI per persona — same six sections, different door).
 */
function RootLanding() {
  const styles = useStyles();
  const { defaultRoute, isLoading } = usePersona();

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  const target = defaultRoute && defaultRoute !== '/home' ? defaultRoute : '/dashboard';
  return <Navigate to={target} replace />;
}

// =============================================================================
// AUTHENTICATED APP COMPONENT
// =============================================================================
function AuthenticatedApp() {
  const styles = useStyles();
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes (no auth, no layout) */}
      <Route path="/" element={user ? <RootLanding /> : <LandingPage />} />
      <Route path="/login" element={user ? <RootLanding /> : <LoginPage />} />

      {/* Routes with MainLayout (authenticated) */}
      <Route element={<MainLayout />}>
        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />

        {/* Compliance */}
        <Route
          path="/compliance"
          element={
            <ProtectedRoute>
              <CompliancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/frameworks"
          element={
            <ProtectedRoute>
              <FrameworksPage />
            </ProtectedRoute>
          }
        />
        {/* §12746 Wave 1 stub — GUID-routed Framework detail (replaces FrameworkDetailDrawer
            for direct-link access; the drawer itself is untouched, chevrons still open it). */}
        <Route
          path="/frameworks/:referenceId"
          element={
            <ProtectedRoute>
              <FrameworkDetailPage />
            </ProtectedRoute>
          }
        />
        {/* Control Coverage disabled — requires manual mapping data */}
        {/* #12772 — GUID-routed Control detail (replaces ControlCoveragePage's inline
            ControlDetailDrawer for direct-link access). Routed independently of
            ControlCoveragePage's own (currently disabled) list route, per the task —
            the detail page works as a standalone deep link regardless. */}
        <Route
          path="/controls/:referenceId"
          element={
            <ProtectedRoute>
              <ControlDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scorecard"
          element={
            <ProtectedRoute>
              <ScorecardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/attestations"
          element={
            <ProtectedRoute>
              <AttestationsPage />
            </ProtectedRoute>
          }
        />

        {/* Security */}
        <Route
          path="/security"
          element={
            <ProtectedRoute>
              <SecurityPage />
            </ProtectedRoute>
          }
        />
        {/* §12746 Wave 1 stubs — GUID-routed Finding/Vulnerability detail (replace the local
            DetailDrawer instances on SecurityPage for direct-link access; the drawers themselves
            are untouched, chevrons still open them). DependenciesPage's Vulnerabilities tab and
            Container Images tab render the SAME Finding/Vulnerability entities — their chevrons
            route here too (Wave 2), rather than a duplicate /dependencies/:referenceId page. */}
        <Route
          path="/security/findings/:referenceId"
          element={
            <ProtectedRoute>
              <FindingDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security/vulnerabilities/:referenceId"
          element={
            <ProtectedRoute>
              <VulnerabilityDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/runs"
          element={
            <ProtectedRoute>
              <PolicyRunsPage />
            </ProtectedRoute>
          }
        />
        {/* §12746 Wave 1 stub — GUID-routed Run detail (replaces RunDetailDrawer for direct-link
            access). Path is `/runs/:referenceId` (not `/policy-runs/:referenceId`) to match the
            list page's actual mounted route above — the nav item is labelled "Decision Log" but
            its path has always been `/runs` (see SideNav.tsx). */}
        <Route
          path="/runs/:referenceId"
          element={
            <ProtectedRoute>
              <RunDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dependencies"
          element={
            <ProtectedRoute>
              <DependenciesPage />
            </ProtectedRoute>
          }
        />

        {/* Operations */}
        <Route
          path="/diagnostic"
          element={
            <ProtectedRoute>
              <DiagnosticPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/evidence"
          element={
            <ProtectedRoute>
              <EvidencePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-room"
          element={
            <ProtectedRoute>
              <ControlRoomPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/environments"
          element={
            <ProtectedRoute>
              <EnvironmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guardrails"
          element={
            <ProtectedRoute>
              <GuardrailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assurance"
          element={
            <ProtectedRoute>
              <AssurancePage />
            </ProtectedRoute>
          }
        />

        {/* Code */}
        <Route
          path="/developer-tools"
          element={
            <ProtectedRoute>
              <DeveloperToolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pr-bot"
          element={
            <ProtectedRoute>
              <PRBotPage />
            </ProtectedRoute>
          }
        />

        {/* Intelligence */}
        <Route
          path="/ai-advisor"
          element={
            <ProtectedRoute>
              <AIAdvisorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/executive-risk"
          element={
            <ProtectedRoute>
              <ExecutiveRiskPage />
            </ProtectedRoute>
          }
        />

        {/* AI Governance */}
        <Route
          path="/ai-governance"
          element={
            <ProtectedRoute>
              <AIGovernanceOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-governance/decisions/:id"
          element={
            <ProtectedRoute>
              <AIDecisionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-governance/constitution"
          element={
            <ProtectedRoute>
              <AIConstitutionRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-governance/simulations"
          element={
            <ProtectedRoute>
              <AISimulationReviewPage />
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/board"
          element={
            <ProtectedRoute>
              <BoardViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />

        {/* Review 008 — Governance Posture, Exceptions, Audit Workspace */}
        <Route
          path="/governance-posture"
          element={
            <ProtectedRoute>
              <GovernancePosturePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exceptions"
          element={
            <ProtectedRoute>
              <PolicyExceptionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <AuditWorkspacePage />
            </ProtectedRoute>
          }
        />

        {/* Integrations */}
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <IntegrationsPage />
            </ProtectedRoute>
          }
        />

        {/* Profile — accessible to ALL authenticated users */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="/settings/users"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <UsersRolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/api-keys"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <ApiKeysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <NotificationPreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/billing"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/merge-gate"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <MergeGateSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/general"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <GeneralSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/sso"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <SSOSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole={['System.Admin', 'Organization.Admin']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* PM Review Features */}
        <Route
          path="/constitution"
          element={
            <ProtectedRoute>
              <ConstitutionEnginePage />
            </ProtectedRoute>
          }
        />

        {/* Phase 3 — Placeholder Pages */}
        <Route
          path="/scanners"
          element={
            <ProtectedRoute>
              <ScannersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/graph"
          element={
            <ProtectedRoute>
              <GraphPage />
            </ProtectedRoute>
          }
        />
        {/* F045: /twin resolved as the "Simulation x2" F5 duplicate of /execution-simulator
            (F035 is the real engine behind Simulation — see iaSurfaceRegistry.ts). */}
        <Route
          path="/twin"
          element={
            <ProtectedRoute>
              <Navigate to="/execution-simulator" replace />
            </ProtectedRoute>
          }
        />
        {/* F031 P5 — Agent Registry (customer AI agent estate as first-class governed assets) */}
        <Route
          path="/agents"
          element={
            <ProtectedRoute>
              <AgentRegistryPage />
            </ProtectedRoute>
          }
        />
        {/* F032 — MCP Governance (MCP servers + tools governed with risk-based approval workflow) */}
        <Route
          path="/mcp-governance"
          element={
            <ProtectedRoute>
              <McpGovernancePage />
            </ProtectedRoute>
          }
        />
        {/* F039 — Autonomous Governance Response (policies, Action Log, Rule-4 approval queue) */}
        <Route
          path="/autonomous-governance"
          element={
            <ProtectedRoute>
              <AutonomousGovernancePage />
            </ProtectedRoute>
          }
        />
        {/* F034 — Runtime Governance Engine (REGE) decision history */}
        <Route
          path="/runtime-governance"
          element={
            <ProtectedRoute>
              <RuntimeGovernancePage />
            </ProtectedRoute>
          }
        />
        {/* F036 — Trust / Execution-Readiness Engine (Identity Trust score) */}
        <Route
          path="/trust"
          element={
            <ProtectedRoute>
              <TrustPage />
            </ProtectedRoute>
          }
        />
        {/* F038 — Governance Drift Detection */}
        <Route
          path="/governance-drift"
          element={
            <ProtectedRoute>
              <GovernanceDriftPage />
            </ProtectedRoute>
          }
        />
        {/* F035 — Execution Safety Simulator */}
        <Route
          path="/execution-simulator"
          element={
            <ProtectedRoute>
              <ExecutionSimulatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collectors"
          element={
            <ProtectedRoute>
              <CollectorsPage />
            </ProtectedRoute>
          }
        />
        {/* F045: /workflow resolved as the "Approvals x2" F5 duplicate of /attestations
            (WorkflowPage: "Shows suppression approval queue, recent decisions..." — the
            same queue AttestationsPage owns). */}
        <Route
          path="/workflow"
          element={
            <ProtectedRoute>
              <Navigate to="/attestations" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/predictive"
          element={
            <ProtectedRoute>
              <PredictivePage />
            </ProtectedRoute>
          }
        />
        {/* F045 (discovered during this pass, not in the spec's original 8): /autonomous
            (AutonomousPage — a 235-line summary widget) duplicates /autonomous-governance
            (AutonomousGovernancePage — the real 1219-line F039 approval-queue implementation).
            Redirected to the canonical surface. */}
        <Route
          path="/autonomous"
          element={
            <ProtectedRoute>
              <Navigate to="/autonomous-governance" replace />
            </ProtectedRoute>
          }
        />
        {/* F045: /learning resolved as the "Frameworks x2" F5 duplicate of /frameworks
            (LearningPage docblock: "Governance Library Page ... Framework browser..."). */}
        <Route
          path="/learning"
          element={
            <ProtectedRoute>
              <Navigate to="/frameworks" replace />
            </ProtectedRoute>
          }
        />

        {/* Review 006 — New Routes */}
        <Route
          path="/governance-loop"
          element={
            <ProtectedRoute>
              <GovernanceLoopPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/essential-eight"
          element={
            <ProtectedRoute>
              <EssentialEightPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-exposure"
          element={
            <ProtectedRoute>
              <DataExposureRadarPage />
            </ProtectedRoute>
          }
        />

        {/* Review 004 — New Routes */}
        {/* F045: /command resolved as the "Control Plane x2" F5 duplicate of /dashboard
            (GovernanceCommandPage docblock: "Route: /command | Nav: Executive Command" —
            answers the same 3 questions HomePage/dashboard does). */}
        <Route
          path="/command"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/attack-paths"
          element={
            <ProtectedRoute>
              <AttackPathsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy-intelligence"
          element={
            <ProtectedRoute>
              <PolicyIntelligenceDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy-intelligence/recommendations"
          element={
            <ProtectedRoute>
              <PolicyRecommendationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policy-intelligence/benchmarks"
          element={
            <ProtectedRoute>
              <BenchmarkInsightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/autorepair"
          element={
            <ProtectedRoute>
              <AutorepairPage />
            </ProtectedRoute>
          }
        />

        {/* AI Code Governance Gate (Review 009) */}
        <Route
          path="/code-governance"
          element={
            <ProtectedRoute>
              <CodeGovernanceDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/code-governance/pull-requests"
          element={
            <ProtectedRoute>
              <CodeGovernancePullRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/code-governance/policies"
          element={
            <ProtectedRoute>
              <CodeGovernancePoliciesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/code-governance/demo"
          element={
            <ProtectedRoute>
              <CodeGovernanceDemoPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

// =============================================================================
// APP COMPONENT
// =============================================================================
function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('ls-theme');
    return (saved === 'light' ? 'light' : 'dark') as ThemeMode;
  });

  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('ls-theme', next);
      return next;
    });
  }, []);

  const currentTheme = themeMode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ mode: themeMode, toggleTheme }}>
      <MsalProvider instance={msalInstance}>
        <FluentProvider theme={currentTheme}>
          <GlobalToaster />
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <TenantProvider>
                <Router>
                  <UserInitProvider>
                    <PersonaProvider>
                      <AuthenticatedApp />
                      <GlobalAssistant />
                    </PersonaProvider>
                  </UserInitProvider>
                </Router>
              </TenantProvider>
            </AuthProvider>
          </QueryClientProvider>
        </FluentProvider>
      </MsalProvider>
    </ThemeContext.Provider>
  );
}

export default App;
